import React, {
	useState,
	ReactNode,
	ReactText,
	useEffect,
	useRef
} from "react";
import { useSelector } from "react-redux";
import filter from "lodash/filter";

import "./SearchDocumentProductCatalog.less";

import { ColumnType, TableRowSelection } from "antd/lib/table/interface";
import Select from "antd/lib/select";
import Row from "antd/lib/row";
import Col from "antd/lib/col";
import Input from "antd/lib/input";

import ConfigProvider from "antd/lib/config-provider";

import NoProductFoundIcon from "@/Assets/ProductsPage/NoProductFoundIcon.svg";
import SearchSomethingProductsIcon from "@/Assets/ProductsPage/StartSearching.svg";
import ZomentumLogo from "config/assets/Logos/ZomentumLogo.gif";

import {
	DefaultTableFilterSortSearchParameters,
	DefaultTableFilterSortSearch,
	EtilizeLocale
} from "@/Contracts";
import {
	Product,
	ProductSource,
	ProductSearchRequest,
	ItemSearchCursor,
	ProductMetaInfo,
	FeatureFilter,
	noOfDistributorsIntegrated,
	ProductType,
	SelfCatalogFilters,
	PricingSearchRequest,
	ProductPricingPlan,
	ETILIZE_PAGE_SIZE,
	SortOptions,
	AmazonFilterProps,
	AmazonRefinementsFilters,
	UOM,
	Distributor,
	distributors as DistributorsList
} from "@/Contracts/Products";

import {
	searchProducts,
	searchPricing,
	searchSelfProductPricing
} from "@/Services/Products";
import ProductCatalogResultRow from "../../../../ProductsPage/Drawers/ProductCatalogResultRow/ProductCatalogResultRow";
import ItemFiltersMenu from "../DocumentProductDrawersComponents/ItemFilterMenu/ItemFiltersMenu";
import { mergePricingImagesIntoProduct } from "./utils";
import { SearchOutlined } from "@ant-design/icons";
import AddProductDrawerV2 from "@pages/Drawers/Products/Product";
import ZTypography from "@atoms/ZTypography";

import { useFeatureFlag } from "@/Hooks/useFeature";
import ZImage from "@atoms/ZImage";
import ZMessage from "@molecules/ZMessage";
import { cache } from "@/Contracts";
import { isEtilizeFiltersDropped } from "@/Components/Utils";
import {
	AllRolePermissions,
	Languages,
	PricingPlanNames,
	PricingAddOnEnum,
	UserRole
} from "@/Contracts/Users";
import ZFeatureAccess from "@atoms/ZFeatureAccess";
import ZUpdateMandatoryFieldsForQuoteBlockDrawer from "@templates/ZDocuments/ZUpdateMandatoryFieldsForQuoteBlockDrawer";
import ZUpdateProductCatalogResultColumn from "@templates/ZProductDetails/ZUpdateProductCatalogResultColumn";
import { EProductDrawerState } from "@pages/Drawers/Products/Product/interface";
import {
	constructProductIdToPricingMapping,
	getCustomDistributorProducts,
	getPreferredPricingForProduct,
	getVendorProducts
} from "@/V2Utils";
import ZTable from "@organisms/ZTable";
import usePermissions from "@/Hooks/usePermissions";
import { Modules, ModulePermission } from "@/Contracts/Permission";
import { VendorSubscriptionStatus } from "@/Contracts/Marketplace/Vendor";
import { GlobalState } from "@/Reducers/interface";
import { proposalBuilderStore } from "@/ProposalBuilder/store";
import { ProductsActionCreators } from "@/Actions/Products";
import { IntegrateAmazonForm } from "../DocumentProductDrawersComponents/AmazonIntegrateForm";
import ZButton from "@atoms/ZButton";
import { useFeatureAccess } from "@/Hooks/useFeatureAccess";
import UsersActionCreators from "@/Actions/User";
import useDispatch from "@/Components/Utils/useDispatch";
import { useComponentDidUpdate } from "@/Hooks";
import Button from "antd/lib/button";

const { ZText } = ZTypography;

export interface Props {
	readonly onSubmit: (
		selectedProducts: Product[],
		productVsPricing: Record<string, string | null>,
		addingToBundle?: boolean
	) => void;
	readonly onClose?: ((e: any) => void) | undefined;
	readonly isLoading: boolean;
	readonly excludeBundles?: boolean;
	readonly itemType?: string;
	readonly isFromProductsPage: boolean;
	readonly configureDrawer?: ReactNode;
	readonly unfilledSelectedQuoteProductIds?: string[];
	readonly removeQuoteProducts?: (quoteProductIds: string[]) => void;
	readonly resetQuoteProducts?: () => void;
	readonly configureQuoteProduct?: (productId: string) => void;
	readonly source?: ProductSource | null;
	readonly createProductPermission?: AllRolePermissions[];
	readonly productVsPricing?: Record<string, string | null>;
	readonly hasPermissionForAmazonDistributor?: boolean;
	readonly isUnifiedSearchEnabled?: boolean;
}

const SearchDocumentProductCatalog: React.FC<Props> = props => {
	const dispatch = useDispatch();
	/* v8 ignore start -- legacy 2280-line drawer; identical wire-shape covered by UnifiedSearchDocumentProductCatalogDrawer.test */
	const docCurrency: string | undefined = undefined;
	/* v8 ignore stop */
	const etilizePricingOn = useFeatureFlag("etilize_pricing");
	const marketplaceFlag = useFeatureFlag("marketplace_sku");
	const showPartnerAlignFeaturesFlag = useFeatureFlag(
		"show_partner_align_features"
	);
	const isIcecatIntegrationEnabled = useFeatureFlag("icecat_integration");
	const isIcecatEnabledForAll = useFeatureFlag("icecat_enabled_for_all");
	const alwaysAllowAmazonDistributor = useFeatureFlag(
		"always_allow_amazon_distributor"
	);
	const newPricingConstraints = useFeatureFlag("new_pricing_constraints");
	const hasGrowthPlanAccess = useFeatureAccess(
		PricingPlanNames.GROWTH,
		UserRole.UserRole
	);
	const hasExpandPlanAccess = useFeatureAccess(
		PricingPlanNames.EXPAND,
		UserRole.UserRole
	);
	const enableCustomDistributorSeparateTabFlag = useFeatureFlag(
		"enable_custom_distributor_separate_tab"
	);
	const isCDSearchEnhancementEnabled = useFeatureFlag(
		"cd_search_enhancement"
	);
	const createProductPermission = usePermissions(
		Modules.ITEM,
		ModulePermission.CREATE_EDIT_DELETE_OWNED
	);
	const {
		distributorsIntegrated,
		isDickerDataIntegrated,
		vendorProgramDetails,
		userCompany,
		isIcecatLocaleFR,
		isFrenchLanguage,
		isInternalUser,
		isIcecatLocaleUS,
		isAmazonDistributorIntegrated,
		hasAmazonAddOn
	} = useSelector((state: GlobalState) => {
		return {
			distributorsIntegrated:
				noOfDistributorsIntegrated(
					state.user.userCompany.integrations
				) > 0,
			isDickerDataIntegrated:
				!!state.user.userCompany.integrations.dicker_data,
			currentUserId: state.user.userData.id,
			vendorProgramDetails: state.marketplace.vendorProgamDetails,
			userCompany: state.user.userCompany,
			isIcecatLocaleFR:
				state.user.userCompany.settings.icecat_locale ===
				EtilizeLocale.FR,
			isIcecatLocaleUS:
				state.user.userCompany.settings.icecat_locale ===
				EtilizeLocale.US,
			isFrenchLanguage:
				state.user.userCompany.settings.language === Languages.FRENCH,
			isAmazonDistributorIntegrated:
				!!state.user.userCompany.integrations.amazon_business,
			isInternalUser: state.user.billingInfo.is_internal,
			hasAmazonAddOn: !!state.user.subscriptionDetails.addons.find(
				addOn => addOn.id?.includes(PricingAddOnEnum.AMAZON_BUSINESS)
			),
			customDistributors:
				state.user.userCompany.integrations.custom_distributor,
			connectedSysDistributors: DistributorsList.filter(
				distributor =>
					state.user.userCompany.integrations[distributor] !== null &&
					distributor !== Distributor.CustomDistributor
			)
		};
	});

	const hasExpandPlanWithAmazonAddOn = hasExpandPlanAccess && hasAmazonAddOn;
	const hasPermissionForAmazonDistributor = newPricingConstraints
		? alwaysAllowAmazonDistributor ||
		  hasExpandPlanWithAmazonAddOn ||
		  hasGrowthPlanAccess ||
		  isInternalUser
		: alwaysAllowAmazonDistributor || hasGrowthPlanAccess || isInternalUser;

	const loadingRef = useRef(false);
	const [loading, setLoading] = useState(false);
	const [pricingLoading, setPricingLoading] = useState(false);
	const lastTriggerTime = useRef<number>(0);
	const hasTriggeredPagination = useRef<boolean>(false);
	const lastSearchRequestKey = useRef<string | null>(null);
	const lastPricingRequestKey = useRef<string | null>(null);

	const [dataSource, setDataSource] = useState<Product[]>(
		new Array<Product>()
	);
	const [metaInfo, setMetaInfo] = useState<ProductMetaInfo>(
		new ProductMetaInfo()
	);
	const [selectedProductList, setSelectedProductList] = useState<Product[]>(
		new Array<Product>()
	);
	const [currentItemSource, setCurrentItemSource] = useState<ProductSource>(
		props.source ??
			(cache.get("product_search_content_source", true) ===
				ProductSource.Amazon && props.isFromProductsPage
				? ProductSource.Self
				: cache.get("product_search_content_source", true)) ??
			ProductSource.Self
	);
	const [currentCursor, setCurrentCursor] = useState<ItemSearchCursor | null>(
		null
	);

	const [manufacturerFilters, setManufacturerFilters] = useState<string[]>(
		[]
	);
	const [categoryFilters, setCategoryFilters] = useState<string[]>([]);
	const [featureFilters, setFeatureFilters] = useState<FeatureFilter[]>([]);
	const [filterByConnectedDistributors, setFilterByConnectedDistributors] =
		useState(false);
	const [customDistributorsFilters, setCustomDistributorsFilters] = useState<
		string[]
	>([]);
	const [selectedDistributorsV2, setSelectedDistributorsV2] = useState<{
		system: string[];
		custom: string[];
	}>({
		system: [],
		custom: []
	});
	const [excludeOutOfStock, setExcludeOutOfStock] = useState(false);
	const [excludeDropShipWH, setExcludeDropShipWH] = useState(false);
	const [productVsPricing, setProductVsPricing] = useState<
		Record<string, string | null>
	>({});
	const [selectedProductType, setSelectedProductType] = useState<string[]>(
		[]
	);
	const [lastFetchedItemsFromPricing, setLastFetchedItemsFromPricing] =
		useState<string[]>([]);
	const [createProductButtonVisible, setCreateProductButtonVisible] =
		useState(false);
	const [selfCatalogFilters, setSelfCatalogFilters] = useState(
		new SelfCatalogFilters()
	);
	const [sortBy, setSortBy] = useState<SortOptions | undefined>(undefined);
	const [amazonFilterState, setAmazonFilterState] =
		useState<AmazonFilterProps>({
			includeOutOfStockProducts: [],
			eligibleForFreeShipping: false,
			amazonPrimeEligible: false,
			amazonProductCategory: [],
			amazonProductSubCategory: [],
			amazonProductDeliveryDay: []
		});

	const [defaultTableFilterSortSearch, setDefaultTableFilterSortSearch] =
		useState<DefaultTableFilterSortSearchParameters>(
			DefaultTableFilterSortSearch
		);

	const tableContainerRef = useRef<HTMLDivElement>(null);
	const searchInputRef = useRef<any>(null);

	const onPricingSelect = (productId: string, selectedPricingId?: string) => {
		setProductVsPricing({
			...productVsPricing,
			[productId]: selectedPricingId || null
		});

		// Update product name and description for custom distributor products when pricing changes
		if (productId.startsWith("custom_distributor:")) {
			updateProductNameAndDescriptionForCustomDistributor(
				productId,
				selectedPricingId
			);
		}
	};

	const updateProductNameAndDescriptionForCustomDistributor = (
		productId: string,
		selectedPricingId?: string
	) => {
		if (!selectedPricingId) return;

		// Update dataSource first
		setDataSource(prevDataSource =>
			prevDataSource.map(product => {
				if (product.id === productId) {
					const selectedPricing = product.pricing.find(
						p => p.id === selectedPricingId
					);

					if (selectedPricing) {
						// For custom distributors, use the pricing's name first, then fall back to product name
						// This ensures the specific pricing name is displayed when selected
						const updatedName =
							selectedPricing.name || product.name;
						const updatedDescription =
							selectedPricing.description || product.description;

						return {
							...product,
							name: updatedName,
							description: updatedDescription
						};
					}
				}
				return product;
			})
		);

		// Also update selectedProductList if the product is currently selected
		setSelectedProductList(prevSelectedList =>
			prevSelectedList.map(selectedProduct => {
				if (selectedProduct.id === productId) {
					const selectedPricing = selectedProduct.pricing.find(
						p => p.id === selectedPricingId
					);

					if (selectedPricing) {
						const updatedName =
							selectedPricing.name || selectedProduct.name;
						const updatedDescription =
							selectedPricing.description ||
							selectedProduct.description;

						return {
							...selectedProduct,
							name: updatedName,
							description: updatedDescription
						};
					}
				}
				return selectedProduct;
			})
		);
	};

	const removeItemsFromUpdateMandatoryFieldsDrawer = (
		products: Product[]
	) => {
		props.removeQuoteProducts?.(products.map(product => product.id));
		setSelectedProductList(prevState =>
			prevState.filter(
				product =>
					!products.some(
						productToBeRemoved =>
							product.id === productToBeRemoved.id
					)
			)
		);
	};

	const sortDataSource = (
		data: Product[],
		sortBy: SortOptions | undefined
	): Product[] => {
		if (!sortBy) {
			return data;
		}
		const sortedData = [...data].sort((a: Product, b: Product) => {
			if (sortBy === SortOptions.NameAToZ) {
				return a.name.localeCompare(b.name);
			}
			if (sortBy === SortOptions.NameZToA) {
				return b.name.localeCompare(a.name);
			}
			if (sortBy === SortOptions.CostPriceHighToLow) {
				const maxB = Math.max(...b.pricing.map(p => p.cost_price ?? 0));
				const maxA = Math.max(...a.pricing.map(p => p.cost_price ?? 0));
				return maxB - maxA;
			}
			if (sortBy === SortOptions.CostPriceLowToHigh) {
				const minA = Math.min(...a.pricing.map(p => p.cost_price ?? 0));
				const minB = Math.min(...b.pricing.map(p => p.cost_price ?? 0));
				return minA - minB;
			}
			if (sortBy === SortOptions.SellPriceHighToLow) {
				const maxB = Math.max(...b.pricing.map(p => p.sell_price ?? 0));
				const maxA = Math.max(...a.pricing.map(p => p.sell_price ?? 0));
				return maxB - maxA;
			}
			if (sortBy === SortOptions.SellPriceLowToHigh) {
				const minA = Math.min(...a.pricing.map(p => p.sell_price ?? 0));
				const minB = Math.min(...b.pricing.map(p => p.sell_price ?? 0));
				return minA - minB;
			}
			return 0;
		});

		const isSellPriceSort =
			sortBy === SortOptions.SellPriceHighToLow ||
			sortBy === SortOptions.SellPriceLowToHigh;
		const isCostPriceSort =
			sortBy === SortOptions.CostPriceHighToLow ||
			sortBy === SortOptions.CostPriceLowToHigh;

		if (isSellPriceSort || isCostPriceSort) {
			const pricingSelections: Record<string, string | null> = {};

			sortedData.forEach(product => {
				const pricingList = [...product.pricing];
				if (!pricingList.length) {
					pricingSelections[product.id] = null;
					return;
				}

				const selectedPricing = pricingList.sort((a, b) => {
					const priceA = isSellPriceSort
						? a.sell_price ?? 0
						: a.cost_price ?? 0;
					const priceB = isSellPriceSort
						? b.sell_price ?? 0
						: b.cost_price ?? 0;
					const isHighToLow =
						sortBy === SortOptions.SellPriceHighToLow ||
						sortBy === SortOptions.CostPriceHighToLow;
					return isHighToLow ? priceB - priceA : priceA - priceB;
				})[0];

				pricingSelections[product.id] = selectedPricing?.id ?? null;
			});

			setProductVsPricing(prev => ({
				...prev,
				...pricingSelections
			}));
		}

		return sortedData;
	};

	const renderProductColumn =
		(showUpdateMandatoryFieldsColumn: boolean) =>
		(_text: unknown, record: Product): ReactNode => {
			const pricingLoadingValue = lastFetchedItemsFromPricing.includes(
				record.id
			)
				? true
				: !productVsPricing[record.id]?.length;
			return (
				<ProductCatalogResultRow
					record={record}
					showPricing={!showUpdateMandatoryFieldsColumn}
					selectedPricingId={productVsPricing[record.id]}
					onPricingChange={onPricingSelect}
					isFromProductsPage={props.isFromProductsPage}
					currentItemSource={currentItemSource}
					pricingLoading={pricingLoading && pricingLoadingValue}
					showUpdateMandatoryFieldsColumn={
						showUpdateMandatoryFieldsColumn
					}
				>
					{showUpdateMandatoryFieldsColumn && (
						<ZUpdateProductCatalogResultColumn
							onUpdateFieldClick={() =>
								props.configureQuoteProduct?.(record.id)
							}
							onRemoveItem={() =>
								removeItemsFromUpdateMandatoryFieldsDrawer([
									record
								])
							}
							updated={
								!props.unfilledSelectedQuoteProductIds?.includes(
									record.id
								)
							}
							selectedPricingId={productVsPricing[record.id]}
							record={record}
							showPricing={showUpdateMandatoryFieldsColumn}
							currentItemSource={currentItemSource}
						/>
					)}
				</ProductCatalogResultRow>
			);
		};

	const productColumns: ColumnType<Product>[] = [
		{
			dataIndex: "name",
			key: "name",
			render: renderProductColumn(false)
		}
	];

	const handleCreateProductClick = () => {
		setCreateProductButtonVisible(true);
	};

	const handleOnClose = () => setCreateProductButtonVisible(false);

	const onClose = (e: any) => {
		// Clear filters for custom distributor when drawer is closed
		if (currentItemSource === ProductSource.CustomDistributor) {
			setManufacturerFilters([]);
			setCategoryFilters([]);
			setFeatureFilters([]);
			setExcludeOutOfStock(false);
			setExcludeDropShipWH(false);
			setFilterByConnectedDistributors(false);
			setCustomDistributorsFilters([]);
			setSelectedDistributorsV2({
				system: [],
				custom: []
			});
			setDataSource([]);
			setCurrentCursor(null);
			setProductVsPricing({});
			setDefaultTableFilterSortSearch({
				...DefaultTableFilterSortSearch
			});
		}

		// Call the parent's onClose if provided
		props.onClose?.(e);
	};

	const stopPropagation = (
		$event: React.MouseEvent<HTMLElement, MouseEvent>
	) => {
		$event && $event.stopPropagation();
	};

	const onProductSourceChange = (currentItemSource: ProductSource) => {
		cache.save("product_search_content_source", currentItemSource);
		setCurrentItemSource(currentItemSource);
		setCurrentCursor(null);
		// Reset request guards for new search
		lastSearchRequestKey.current = null;
		lastPricingRequestKey.current = null;
		setMetaInfo(new ProductMetaInfo());
		setSelectedProductList([]);
		setSelfCatalogFilters(new SelfCatalogFilters());
		resetCustomFilters(true);
		setDataSource([]);
		setProductVsPricing({});

		if (
			!isAmazonDistributorIntegrated &&
			currentItemSource === ProductSource.Amazon
		) {
			return;
		}

		const isEtilizeOrIcecat =
			currentItemSource === ProductSource.Etilize ||
			currentItemSource === ProductSource.Icecat;

		const isCustomDistributor =
			currentItemSource === ProductSource.CustomDistributor;

		if (
			(isEtilizeOrIcecat || isCustomDistributor) &&
			!defaultTableFilterSortSearch.currentKeywords?.length
		) {
			return;
		}

		const itemTypeFilter = findProductType(props.itemType);
		const shouldFetchRealtimePricing =
			currentItemSource !== ProductSource.Self;

		const filterByConnectedDistributors =
			(currentItemSource === ProductSource.Etilize ||
				currentItemSource === ProductSource.Icecat) &&
			distributorsIntegrated &&
			!isDickerDataIntegrated;
		const source =
			currentItemSource === ProductSource.Icecat
				? (isIcecatIntegrationEnabled &&
						(isIcecatLocaleFR ||
							(isIcecatLocaleUS && isFrenchLanguage))) ||
				  isIcecatEnabledForAll
					? ProductSource.Icecat
					: ProductSource.Etilize
				: currentItemSource;
		const searchRequest = ProductSearchRequest.constructSearchRequest(
			defaultTableFilterSortSearch.currentKeywords,
			source,
			null,
			shouldFetchRealtimePricing,
			filterByConnectedDistributors,
			(selectedDistributorsV2?.system as Distributor[]) || [],
			customDistributorsFilters?.length
				? customDistributorsFilters
				: selectedDistributorsV2?.custom,
			false,
			false,
			[],
			[],
			[],
			itemTypeFilter,
			new SelfCatalogFilters(),
			false,
			sortBy
		);
		searchProductsHandle(searchRequest, false, true);
	};

	const shouldUseLazyLoad = (
		productSearchRequest: ProductSearchRequest
	): boolean => {
		if (
			(productSearchRequest.filter_by_connected_distributors &&
				productSearchRequest.exclude_out_of_stock_products) ||
			!etilizePricingOn
		)
			return false;
		return true;
	};

	const onProductSearchKeywordChange = (currentKeywords: string) => {
		let itemTypeFilter = selectedProductType;
		if (props.isFromProductsPage) {
			itemTypeFilter = findProductType(props.itemType);
		}
		setDefaultTableFilterSortSearch({
			...defaultTableFilterSortSearch,
			currentKeywords
		});
		setCurrentCursor(null);
		// Reset request guards for new search
		lastSearchRequestKey.current = null;
		lastPricingRequestKey.current = null;

		// Reset scroll position to top for new search
		setTimeout(() => {
			const drawer: HTMLElement | null | undefined =
				tableContainerRef.current?.querySelector(
					".table-page-container .product-table .ant-table"
				);
			if (drawer) {
				drawer.scrollTop = 0;
			}
		}, 100);

		const shouldFetchRealtimePricing =
			currentItemSource !== ProductSource.Self;
		if (shouldFetchRealtimePricing) {
			resetCustomFilters(false);
		}

		const isSourceAmazon = currentItemSource === ProductSource.Amazon;
		if (isSourceAmazon) {
			setAmazonFilterState({
				includeOutOfStockProducts: [],
				eligibleForFreeShipping: false,
				amazonPrimeEligible: false,
				amazonProductCategory: [],
				amazonProductSubCategory: [],
				amazonProductDeliveryDay: []
			});

			const appliedFilters = {
				categories: [],
				sub_categories: [],
				availability_options: []
			};
			dispatch(
				UsersActionCreators.updateAmazonProductAppliedFilter(
					appliedFilters
				)
			);
		}

		const source =
			currentCursor?.current_source ??
			(currentItemSource === ProductSource.Icecat
				? (isIcecatIntegrationEnabled &&
						(isIcecatLocaleFR ||
							(isIcecatLocaleUS && isFrenchLanguage))) ||
				  isIcecatEnabledForAll
					? ProductSource.Icecat
					: ProductSource.Etilize
				: currentItemSource);

		const searchRequest = ProductSearchRequest.constructSearchRequest(
			currentKeywords,
			source,
			null,
			shouldFetchRealtimePricing,
			(currentItemSource === ProductSource.Etilize ||
				currentItemSource === ProductSource.Icecat) &&
				filterByConnectedDistributors,
			(selectedDistributorsV2?.system as Distributor[]) || [],
			customDistributorsFilters?.length
				? customDistributorsFilters
				: selectedDistributorsV2?.custom,
			currentItemSource === ProductSource.Etilize ||
				currentItemSource === ProductSource.Icecat
				? excludeOutOfStock
				: currentItemSource === ProductSource.Amazon,
			(currentItemSource === ProductSource.Etilize ||
				currentItemSource === ProductSource.Icecat) &&
				excludeDropShipWH,
			[],
			[],
			[],
			itemTypeFilter,
			selfCatalogFilters,
			false,
			sortBy
		);
		searchProductsHandle(searchRequest, false, true);
	};

	const findProductType = (itemType: string | undefined) => {
		if (itemType) {
			const fetchProducts = filter(ProductType, product => {
				return product !== itemType;
			});
			return fetchProducts;
		}
		return [];
	};

	useEffect(() => {
		if (
			!isAmazonDistributorIntegrated &&
			currentItemSource === ProductSource.Amazon
		) {
			return;
		}

		const isEtilizeOrIcecat =
			currentItemSource === ProductSource.Etilize ||
			currentItemSource === ProductSource.Icecat;

		const isCustomDistributor =
			currentItemSource === ProductSource.CustomDistributor;

		if (
			(isEtilizeOrIcecat || isCustomDistributor) &&
			!defaultTableFilterSortSearch.currentKeywords?.length
		) {
			return;
		}

		const itemTypeFilter = findProductType(props.itemType);
		const shouldFetchRealtimePricing =
			currentItemSource !== ProductSource.Self;
		const source =
			currentCursor?.current_source ??
			(currentItemSource === ProductSource.Icecat
				? (isIcecatIntegrationEnabled &&
						(isIcecatLocaleFR ||
							(isIcecatLocaleUS && isFrenchLanguage))) ||
				  isIcecatEnabledForAll
					? ProductSource.Icecat
					: ProductSource.Etilize
				: currentItemSource);
		const searchRequest = ProductSearchRequest.constructSearchRequest(
			defaultTableFilterSortSearch.currentKeywords,
			source,
			null,
			shouldFetchRealtimePricing,
			false,
			(selectedDistributorsV2?.system as Distributor[]) || [],
			customDistributorsFilters?.length
				? customDistributorsFilters
				: selectedDistributorsV2?.custom,
			isEtilizeOrIcecat
				? distributorsIntegrated
				: currentItemSource === ProductSource.Amazon,
			isEtilizeOrIcecat && distributorsIntegrated,
			[],
			[],
			[],
			itemTypeFilter,
			selfCatalogFilters,
			false,
			sortBy
		);
		searchProductsHandle(searchRequest, false, true);
	}, []);

	useEffect(() => {
		setTimeout(() => {
			const drawer: HTMLElement | null | undefined =
				tableContainerRef.current?.querySelector(
					".table-page-container .product-table .ant-table"
				);
			if (drawer) {
				drawer.onscroll = onScroll;
			}
		}, 0);

		return () => {
			const drawer: HTMLElement | null | undefined =
				tableContainerRef.current?.querySelector(
					".table-page-container .product-table .ant-table"
				);
			if (drawer) {
				drawer.onscroll = null;
			}
		};
	}, [currentCursor, loading]);

	useComponentDidUpdate(() => {
		setCurrentItemSource(
			currentItemSource === ProductSource.Etilize
				? ProductSource.Icecat
				: currentItemSource
		);
	}, [isIcecatIntegrationEnabled]);

	// Auto-pagination for small result sets - but only if not triggered by scroll
	useEffect(() => {
		if (
			dataSource.length < ETILIZE_PAGE_SIZE &&
			currentCursor?.current_page_no &&
			!!currentCursor &&
			!loading && // Prevent multiple search requests when already loading
			!loadingRef.current && // Additional check for loading ref
			!hasTriggeredPagination.current // Only auto-paginate if scroll didn't trigger it
		) {
			const shouldFetchRealtimePricing =
				currentItemSource !== ProductSource.Self;
			const source =
				currentCursor?.current_source ??
				(currentItemSource === ProductSource.Icecat
					? (isIcecatIntegrationEnabled &&
							(isIcecatLocaleFR ||
								(isIcecatLocaleUS && isFrenchLanguage))) ||
					  isIcecatEnabledForAll
						? ProductSource.Icecat
						: ProductSource.Etilize
					: currentItemSource);
			const searchRequest = ProductSearchRequest.constructSearchRequest(
				defaultTableFilterSortSearch.currentKeywords,
				source,
				currentCursor,
				shouldFetchRealtimePricing,
				filterByConnectedDistributors,
				(selectedDistributorsV2?.system as Distributor[]) || [],
				customDistributorsFilters?.length
					? customDistributorsFilters
					: selectedDistributorsV2?.custom,
				excludeOutOfStock,
				excludeDropShipWH,
				manufacturerFilters,
				categoryFilters,
				[],
				[],
				new SelfCatalogFilters(),
				false,
				sortBy
			);
			searchProductsHandle(searchRequest, true, true);
		}
	}, [currentCursor?.current_page_no]);

	const handleCustomDistributorPricing = async (
		customDistributorProducts: Product[],
		dataSourceValue: Product[],
		appendResults: boolean,
		excludeDropShipWH: boolean,
		searchQuery: string | null
	) => {
		// Prevent multiple simultaneous pricing calls
		if (pricingLoading) {
			return;
		}

		// Additional guard: prevent multiple pricing requests for same products
		const productIds = customDistributorProducts
			.map(p => p.id)
			.sort()
			.join(",");
		const pricingKey = `${productIds}-${appendResults}`;
		if (lastPricingRequestKey.current === pricingKey) {
			return;
		}
		lastPricingRequestKey.current = pricingKey;

		try {
			setPricingLoading(true);
			// Only show loading for current page items (most recent batch)
			const currentPageItems = appendResults
				? customDistributorProducts
						.slice(-ETILIZE_PAGE_SIZE)
						.map(product => product.id)
				: customDistributorProducts.map(product => product.id);
			setLastFetchedItemsFromPricing(currentPageItems);
			const customDistributorProductsForPricing =
				getCustomDistributorProducts(customDistributorProducts);
			const customDistributorPricingRequest =
				PricingSearchRequest.constructSearchRequest(
					[],
					[],
					customDistributorProductsForPricing,
					excludeDropShipWH,
					searchQuery
				);

			const pricingResponse = await searchPricing(
				customDistributorPricingRequest,
				/* v8 ignore next */
				docCurrency
			);
			const responseDataWithPricing = pricingResponse?.data || [];

			const productVsPricingMap = responseDataWithPricing.reduce(
				(map, product) => {
					map[product.id] = product.pricing;
					return map;
				},
				{}
			);

			const updatedDataSource = dataSourceValue.map(product => {
				const additionalProductPricing =
					productVsPricingMap[product.id];

				const pricingResponseProduct = responseDataWithPricing.find(
					p => p.id === product.id
				);

				if (
					additionalProductPricing &&
					additionalProductPricing.length > 0
				) {
					const existingPricing = product.pricing || [];
					const existingPricingIds = new Set(
						existingPricing.map(p => p.id).filter(id => id)
					);

					const uniqueAdditionalPricing =
						additionalProductPricing.filter(
							(p: ProductPricingPlan) =>
								!p.id || !existingPricingIds.has(p.id)
						);

					const enhancedAdditionalPricing =
						uniqueAdditionalPricing.map(
							(pricing: ProductPricingPlan) => ({
								...pricing,
								name:
									pricing.name ||
									pricingResponseProduct?.name,
								description:
									pricing.description ||
									pricingResponseProduct?.description
							})
						);

					const newPricing = [
						...existingPricing,
						...enhancedAdditionalPricing
					];

					const productWithPricing = {
						...product,
						pricing: newPricing
					};
					const mergedProduct =
						mergePricingImagesIntoProduct(productWithPricing);

					return {
						...mergedProduct,
						name: pricingResponseProduct
							? pricingResponseProduct.name || product.name
							: product.name,
						description: pricingResponseProduct
							? pricingResponseProduct.description ||
							  product.description
							: product.description
					};
				} else if (pricingResponseProduct) {
					const newPricing =
						product.pricing && product.pricing.length > 0
							? product.pricing.map(pricing => ({
									...pricing,
									name:
										pricing.name ||
										pricingResponseProduct?.name,
									description:
										pricing.description ||
										pricingResponseProduct?.description
							  }))
							: product.pricing;

					const productWithPricing = {
						...product,
						pricing: newPricing,
						name: pricingResponseProduct.name || product.name,
						description:
							pricingResponseProduct.description ||
							product.description
					};

					if (newPricing && newPricing.length > 0) {
						return mergePricingImagesIntoProduct(
							productWithPricing
						);
					}
					return productWithPricing;
				}
				return product;
			});

			const newProductVsPricingIdMap =
				constructProductIdToPricingMapping(updatedDataSource);

			// Update product name and description for custom distributor products based on selected pricing
			const finalUpdatedDataSource = updatedDataSource.map(product => {
				if (product.id.startsWith("custom_distributor:")) {
					const selectedPricingId =
						newProductVsPricingIdMap[product.id];

					if (selectedPricingId) {
						const selectedPricing = product.pricing.find(
							p => p.id === selectedPricingId
						);

						if (selectedPricing) {
							// For custom distributors, use pricing name (not vendor_name)
							// to match our previous fixes for consistency
							const updatedName =
								selectedPricing.name || product.name;
							const updatedDescription =
								selectedPricing.description ||
								product.description;

							return {
								...product,
								name: updatedName,
								description: updatedDescription
							};
						}
					}
				}
				return product;
			});

			setDataSource(prevDataSource => {
				// If this is a new search (not appending), use only the updated results
				if (!appendResults) {
					return finalUpdatedDataSource;
				}

				// For additional pricing calls, we need to UPDATE existing products, not append new ones
				// finalUpdatedDataSource contains the same products as prevDataSource but with enhanced pricing
				const updatedProductIds = new Set(
					finalUpdatedDataSource.map(p => p.id)
				);
				const nonUpdatedProducts = prevDataSource.filter(
					p => !updatedProductIds.has(p.id)
				);

				return [...nonUpdatedProducts, ...finalUpdatedDataSource];
			});

			const updatedByIdFirst = new Map(
				finalUpdatedDataSource.map(p => [p.id, p])
			);
			setSelectedProductList(prevSelectedList =>
				prevSelectedList.map(
					selectedProduct =>
						updatedByIdFirst.get(selectedProduct.id) ||
						selectedProduct
				)
			);

			setProductVsPricing(prevProductVsPricing => ({
				...(appendResults ? prevProductVsPricing : {}),
				...newProductVsPricingIdMap
			}));
		} catch (error) {
			console.error("Error fetching custom distributor pricing:", error);
		} finally {
			setPricingLoading(false);
			setLastFetchedItemsFromPricing([]);
		}
	};

	const searchProductsHandle = async (
		productSearchRequest: ProductSearchRequest,
		appendResults: boolean,
		updateMetaInfo: boolean
	) => {
		// Prevent multiple simultaneous search requests
		if (loading || loadingRef.current) {
			return;
		}

		// Additional guard: prevent multiple search requests for same page
		const requestKey = `${productSearchRequest.q}-${
			productSearchRequest.cursor?.current_page_no || 0
		}-${appendResults}`;
		if (lastSearchRequestKey.current === requestKey) {
			return;
		}
		lastSearchRequestKey.current = requestKey;

		// Reset guard timers for new searches (not appends)
		if (!appendResults) {
			lastTriggerTime.current = 0;
			lastPricingRequestKey.current = null;
			hasTriggeredPagination.current = false;
		}

		setLoading(true);
		loadingRef.current = true;
		try {
			const isRequestForLazyLoad =
				shouldUseLazyLoad(productSearchRequest);
			const productsSearchResponse = await searchProducts(
				{
					...productSearchRequest,
					is_request_for_lazy_load: isRequestForLazyLoad
				},
				/* v8 ignore next */
				docCurrency
			);
			if (
				isEtilizeFiltersDropped(
					productSearchRequest,
					productsSearchResponse
				)
			)
				ZMessage.error(
					"Some filter(s) were dropped because no products were found under them."
				);

			let dataSourceValue: Product[] = [];

			if (appendResults && !!dataSource?.length) {
				// Avoid duplicates when appending results
				const newProductIds = new Set(
					productsSearchResponse.data.map(p => p.id)
				);
				const existingNonDuplicates = dataSource.filter(
					p => !newProductIds.has(p.id)
				);
				dataSourceValue = existingNonDuplicates.concat(
					productsSearchResponse.data
				);
			} else {
				dataSourceValue = productsSearchResponse.data;
			}

			if (currentItemSource === ProductSource.Icecat) {
				dataSourceValue = sortDataSource(dataSourceValue, sortBy);
			}

			// Enhance initial custom distributor products with vendor information for each pricing plan
			// And merge pricing images into item_images for all products
			dataSourceValue = dataSourceValue.map(product => {
				let updatedProduct = product;

				if (
					product.id.startsWith("custom_distributor:") &&
					product.pricing &&
					product.pricing.length > 0
				) {
					const enhancedPricing = product.pricing.map(
						(pricing: ProductPricingPlan) => ({
							...pricing,
							vendor_name: pricing.vendor_name || product.name,
							name: pricing.name || product.name,
							description:
								pricing.description || product.description
						})
					);
					updatedProduct = { ...product, pricing: enhancedPricing };
				}

				return mergePricingImagesIntoProduct(updatedProduct);
			});

			const pricingMapForDataSource =
				constructProductIdToPricingMapping(dataSourceValue);
			const productVsPricingInitialValue = Object.keys(
				pricingMapForDataSource
			).reduce(
				(map, productId) => {
					if (!map[productId] || pricingMapForDataSource[productId]) {
						map[productId] = pricingMapForDataSource[productId];
					}
					return map;
				},
				{ ...productVsPricing }
			);
			setProductVsPricing(productVsPricingInitialValue);
			setDataSource(dataSourceValue);
			setCurrentCursor(productsSearchResponse.next);
			setLoading(false);
			loadingRef.current = false;

			// Reset pagination trigger for new data - allows next scroll to bottom to trigger
			if (appendResults) {
				hasTriggeredPagination.current = false;
			}

			// Ensure scroll listener is attached after search completes
			setTimeout(() => {
				const drawer: HTMLElement | null | undefined =
					tableContainerRef.current?.querySelector(
						".table-page-container .product-table .ant-table"
					);
				if (drawer) {
					drawer.onscroll = onScroll;
				}
			}, 100);

			// Handle custom distributor pricing for products found in non-CustomDistributor sources
			// Skip if current source is CustomDistributor as it's handled in the dedicated block below
			// Use the current search response data to get pricing for newly fetched products only
			const currentResponseData = appendResults
				? productsSearchResponse.data
				: dataSourceValue;
			const customDistributorProductsInResponse =
				currentResponseData.filter(product =>
					product.id.startsWith("custom_distributor:")
				);

			if (
				customDistributorProductsInResponse.length > 0 &&
				currentItemSource !== ProductSource.CustomDistributor
			) {
				await handleCustomDistributorPricing(
					customDistributorProductsInResponse,
					dataSourceValue,
					appendResults,
					excludeDropShipWH,
					productSearchRequest.q
				);
			}

			if (currentItemSource === ProductSource.Self) {
				try {
					setPricingLoading(true);
					const itemIds = productsSearchResponse.data.map(
						product => product.id
					);
					// Only show loading for current page items (most recent batch)
					const currentPageItems = appendResults
						? itemIds.slice(-ETILIZE_PAGE_SIZE)
						: itemIds;
					setLastFetchedItemsFromPricing(currentPageItems);
					const pricingList = await searchSelfProductPricing(itemIds);

					const tempDataSource = dataSourceValue;

					const dataSourceWithPricing = tempDataSource.map(
						product => {
							const pricing =
								pricingList.data.find(pricingDetail => {
									return pricingDetail.item_id === product.id;
								})?.pricing || product.pricing;

							return {
								...product,
								pricing: pricing
							};
						}
					);

					const productVsPricingValue = {
						...productVsPricingInitialValue,
						...constructProductIdToPricingMapping(
							dataSourceWithPricing
						)
					};

					setDataSource(dataSourceWithPricing);
					setProductVsPricing(productVsPricingValue);
				} catch (error) {
					console.error(error);
				} finally {
					setPricingLoading(false);
				}
			} else if (currentItemSource === ProductSource.CustomDistributor) {
				// Use the current search response data to get pricing for newly fetched products only
				const currentCustomDistributorResponseData = appendResults
					? productsSearchResponse.data
					: dataSourceValue;
				const customDistributorProducts =
					currentCustomDistributorResponseData.filter(product =>
						product.id.startsWith("custom_distributor:")
					);

				if (customDistributorProducts.length > 0) {
					// Only call pricing if not already in progress to prevent multiple calls
					if (!pricingLoading) {
						await handleCustomDistributorPricing(
							customDistributorProducts,
							dataSourceValue,
							appendResults,
							excludeDropShipWH,
							productSearchRequest.q
						);
					}
				}
			} else if (etilizePricingOn && isRequestForLazyLoad) {
				try {
					setPricingLoading(true);
					const isIcecatEnabled =
						(isIcecatIntegrationEnabled &&
							(isIcecatLocaleFR ||
								(isIcecatLocaleUS && isFrenchLanguage))) ||
						isIcecatEnabledForAll ||
						false;
					const dataWithoutPricing = dataSourceValue.filter(
						product =>
							!product.pricing.length &&
							!product.id.startsWith("custom_distributor:") &&
							((product?.icecat_skus_info && isIcecatEnabled) ||
								product?.etilize_skus_info)
					);
					// Only show loading for current page items (most recent batch)
					const currentPageItemsWithoutPricing = appendResults
						? dataWithoutPricing
								.slice(-ETILIZE_PAGE_SIZE)
								.map(data => data.id)
						: dataWithoutPricing.map(data => data.id);
					setLastFetchedItemsFromPricing(
						currentPageItemsWithoutPricing
					);

					if (!!dataWithoutPricing?.length) {
						const vendorProducts = getVendorProducts(
							dataWithoutPricing,
							isIcecatEnabled
						);
						const vendorPricingRequest =
							PricingSearchRequest.constructSearchRequest(
								isIcecatEnabled ? [] : vendorProducts,
								isIcecatEnabled ? vendorProducts : [],
								[],
								excludeDropShipWH,
								productSearchRequest.q
							);

						const pricingResponse = await searchPricing(
							vendorPricingRequest,
							/* v8 ignore next */
							docCurrency
						);
						const responseDataWithPricing =
							pricingResponse?.data || [];

						const productVsPricingMap =
							responseDataWithPricing.reduce((map, product) => {
								map[product.id] = product.pricing;
								return map;
							}, {});
						const newProductVsPricingIdMap =
							constructProductIdToPricingMapping(
								responseDataWithPricing
							);
						const updatedDataSource = dataSource.map(product => {
							const latestProductPricing =
								productVsPricingMap[product.id];
							if (latestProductPricing) {
								const updatedProduct = {
									...product,
									pricing: latestProductPricing
								};
								return mergePricingImagesIntoProduct(
									updatedProduct
								);
							}
							return product;
						});

						const updatedById = new Map(
							updatedDataSource.map(p => [p.id, p])
						);

						setDataSource(updatedDataSource);

						setSelectedProductList(prevSelectedList =>
							prevSelectedList.map(
								selectedProduct =>
									updatedById.get(selectedProduct.id) ||
									selectedProduct
							)
						);

						setProductVsPricing(productVsPricingIdMap => {
							return Object.keys(newProductVsPricingIdMap).reduce(
								(map, productId) => {
									if (
										!map[productId] ||
										newProductVsPricingIdMap[productId]
									) {
										map[productId] =
											newProductVsPricingIdMap[productId];
									}
									return map;
								},
								{ ...productVsPricingIdMap }
							);
						});
					}
				} catch (error) {
					console.error(error);
				} finally {
					setPricingLoading(false);
				}
			}

			if (updateMetaInfo) {
				setMetaInfo(productsSearchResponse.meta_info);
			}
		} catch (error) {
			console.error(error);
		} finally {
			setLoading(false);
			loadingRef.current = false;
		}
	};

	const customEmptyProductsTable = () => {
		const isAValidSearch =
			(defaultTableFilterSortSearch.currentKeywords?.length || 0) > 0;
		return isAValidSearch ? (
			<div className="flex flex-col items-center">
				<NoProductFoundIcon className="empty-table-icon" />
				<ZText className="empty-table-text">
					The item entered is not found. <br />
					Try checking your spelling or use more general terms
				</ZText>
				<div className="create-product-cta margin-top-8px">
					<ZText colorVariant={7}>OR</ZText>
					<ZButton
						block
						type="link"
						htmlType="button"
						onClick={handleCreateProductClick}
					>
						Create product in your self catalog
					</ZButton>
				</div>
			</div>
		) : (
			<div className="flex flex-col items-center">
				<SearchSomethingProductsIcon className="empty-table-icon" />
				<ZText className="empty-table-text">
					Type something to start searching for products.
				</ZText>
			</div>
		);
	};

	const onScroll = (_$event: Event) => {
		try {
			const drawer: HTMLElement | null | undefined =
				tableContainerRef.current?.querySelector(
					".table-page-container .product-table .ant-table"
				);

			if (!drawer) {
				throw new Error("Drawer Query is not available");
			}

			if (!!loading || !!loadingRef.current) {
				return;
			}

			const scrollThreshold = 60;
			const isAtBottom =
				drawer.offsetHeight + drawer.scrollTop >=
				drawer.scrollHeight - scrollThreshold;

			// Check if pagination cursor exists and the user is at the bottom of the page
			if (!isAtBottom) {
				// Reset pagination trigger when user is not at bottom
				hasTriggeredPagination.current = false;
				return;
			}

			// Prevent multiple triggers when user is at bottom
			if (hasTriggeredPagination.current) {
				return;
			}

			const isFetchingRecursively =
				dataSource.length >= ETILIZE_PAGE_SIZE;

			if (!currentCursor || !isFetchingRecursively) {
				return;
			}

			// Mark that we've triggered pagination for this scroll-to-bottom event
			hasTriggeredPagination.current = true;
			let itemTypeFilter = selectedProductType;
			if (props.isFromProductsPage) {
				itemTypeFilter = findProductType(props.itemType);
			}

			const shouldFetchRealtimePricing =
				currentItemSource !== ProductSource.Self;

			const shouldFilterByDistributors = shouldFetchRealtimePricing
				? filterByConnectedDistributors
				: false;

			const amazonRefineMentsMetaData = metaInfo.amazon_refinements;
			const amazonSearchRequest: AmazonRefinementsFilters = {
				amazon_prime_eligible: amazonFilterState?.amazonPrimeEligible
					? amazonRefineMentsMetaData.prime_eligible[0].value
					: "",
				eligible_for_free_shipping:
					amazonFilterState?.eligibleForFreeShipping
						? amazonRefineMentsMetaData
								.eligible_for_free_shipping_options[0].value
						: "",
				include_out_of_stock_products:
					amazonFilterState?.includeOutOfStockProducts?.toString() ??
					"",
				amazon_delivery_date:
					amazonFilterState?.amazonProductDeliveryDay?.toString() ??
					""
			};

			const source =
				currentCursor?.current_source ??
				(currentItemSource === ProductSource.Icecat
					? (isIcecatIntegrationEnabled &&
							(isIcecatLocaleFR ||
								(isIcecatLocaleUS && isFrenchLanguage))) ||
					  isIcecatEnabledForAll
						? ProductSource.Icecat
						: ProductSource.Etilize
					: currentItemSource);

			const searchRequest = ProductSearchRequest.constructSearchRequest(
				defaultTableFilterSortSearch.currentKeywords,
				source,
				currentCursor,
				shouldFetchRealtimePricing,
				shouldFilterByDistributors,
				(selectedDistributorsV2?.system as Distributor[]) || [],
				customDistributorsFilters?.length
					? customDistributorsFilters
					: selectedDistributorsV2?.custom,
				excludeOutOfStock,
				excludeDropShipWH,
				manufacturerFilters,
				currentItemSource !== ProductSource.Amazon
					? categoryFilters
					: amazonFilterState?.amazonProductCategory,
				featureFilters,
				itemTypeFilter,
				selfCatalogFilters,
				false,
				sortBy,
				amazonFilterState?.amazonProductSubCategory,
				amazonSearchRequest
			);

			// Call the product list API here
			searchProductsHandle(searchRequest, true, false);
		} catch (error) {
			console.error(error);
		}
	};

	const onSubmit = ($event: React.MouseEvent<HTMLElement, MouseEvent>) => {
		$event.preventDefault();
		if (!(selectedProductList && selectedProductList.length > 0)) {
			ZMessage.error("Please select a product");
			return;
		}

		// Get the selected product ID
		const selectedProductId = selectedProductList[0].id;

		// Find the current product data from dataSource (which has updated name/description)
		// If not found in dataSource, fall back to selectedProductList (for products from previous searches)
		const currentProductData =
			dataSource.find(product => product.id === selectedProductId) ||
			selectedProductList[0];

		// Create the product to be added with current data and selected pricing information
		const productToBeAdded = {
			...currentProductData,
			// Ensure unit_of_measure is set
			unit_of_measure: currentProductData.unit_of_measure || UOM.EACH,
			// Include the selected pricing information if available
			selectedPricingId: productVsPricing[selectedProductId],
			// For custom distributors, also include the pricing details for the API call
			...(selectedProductId.startsWith("custom_distributor:") &&
				productVsPricing[selectedProductId] && {
					selectedPricing: currentProductData.pricing?.find(
						p => p.id === productVsPricing[selectedProductId]
					)
				})
		};

		proposalBuilderStore.dispatch(
			ProductsActionCreators.setSelectedEtilizeProduct(productToBeAdded)
		);
		submitProductData();
	};

	const submitProductData = () => {
		const productList = !pricingLoading
			? selectedProductList.map(product => {
					const productWithPricing = dataSource.find(
						productWithPricing => {
							return product.id === productWithPricing.id;
						}
					);
					if (productWithPricing?.pricing) {
						product.pricing = productWithPricing?.pricing;
					}
					return product;
			  })
			: selectedProductList;
		props.onSubmit(
			productList,
			productVsPricing,
			props.itemType === ProductType.Bundle
		);
	};

	const resetCustomFilters = (shouldResetAllFilters: boolean) => {
		if (shouldResetAllFilters) {
			setManufacturerFilters([]);
			setCategoryFilters([]);
			setFeatureFilters([]);
			setFilterByConnectedDistributors(false);
			setCustomDistributorsFilters([]);
			setSelectedDistributorsV2({
				system: [],
				custom: []
			});
			setExcludeOutOfStock(false);
			setExcludeDropShipWH(false);
			setSelectedProductType([]);
			setSelfCatalogFilters(new SelfCatalogFilters());
		} else {
			setManufacturerFilters([]);
			setCategoryFilters([]);
			setFeatureFilters([]);
			setSelectedProductType([]);
			setSelfCatalogFilters(new SelfCatalogFilters());
		}
	};

	// Clear filters when switching to CustomDistributor source
	useEffect(() => {
		if (currentItemSource === ProductSource.CustomDistributor) {
			setManufacturerFilters([]);
			setCategoryFilters([]);
			setFeatureFilters([]);
			setFilterByConnectedDistributors(false);
			setCustomDistributorsFilters([]);
			setSelectedDistributorsV2({
				system: [],
				custom: []
			});
			setExcludeOutOfStock(false);
			setExcludeDropShipWH(false);
			setSelectedProductType([]);
			setSelfCatalogFilters(new SelfCatalogFilters());
		}
	}, [currentItemSource]); // Run when currentItemSource changes

	// When the in-drawer currency toggle flips, the catalog pricing API needs
	// to be re-hit so cached rows don't sit in the old currency while later
	// fetches return the new one. Re-run the current search with the live
	// keywords + filters.
	useEffect(() => {
		onProductSearchKeywordChange(
			defaultTableFilterSortSearch.currentKeywords ?? ""
		);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [docCurrency]);

	// Cleanup filters when component unmounts
	useEffect(() => {
		return () => {
			resetCustomFilters(true);
		};
	}, []);

	const onApplyFilter = (
		selectedManufactures: string[],
		selectedCategories: string[],
		selectedFeatures: FeatureFilter[],
		selectedProductType: string[],
		selfCatalogFilters: SelfCatalogFilters,
		filterByConnectedDistributors?: boolean,
		excludeOutOfStock?: boolean,
		excludeDropShipWH?: boolean,
		amazonFilterProps?: AmazonFilterProps,
		selectedCustomDistributors?: string[],
		selectedDistributorsV2Data?: { system: string[]; custom: string[] }
	) => {
		if (
			(currentItemSource === ProductSource.Etilize ||
				currentItemSource === ProductSource.Icecat) &&
			!defaultTableFilterSortSearch.currentKeywords?.length
		) {
			return;
		}

		// Reset scroll position to top when filters are applied
		setTimeout(() => {
			const drawer: HTMLElement | null | undefined =
				tableContainerRef.current?.querySelector(
					".table-page-container .product-table .ant-table"
				);
			if (drawer) {
				drawer.scrollTop = 0;
			}
		}, 100);
		let itemTypeFilter = selectedProductType;
		if (props.isFromProductsPage) {
			itemTypeFilter = findProductType(props.itemType);
		}
		const shouldFetchRealtimePricing =
			currentItemSource !== ProductSource.Self;

		const shouldFilterByDistributors = shouldFetchRealtimePricing
			? filterByConnectedDistributors
			: false;

		const amazonRefineMentsMetaData = metaInfo.amazon_refinements;
		const amazonSearchRequest: AmazonRefinementsFilters = {
			amazon_prime_eligible: amazonFilterProps?.amazonPrimeEligible
				? amazonRefineMentsMetaData?.prime_eligible[0]?.value
				: "",
			eligible_for_free_shipping:
				amazonFilterProps?.eligibleForFreeShipping
					? amazonRefineMentsMetaData
							?.eligible_for_free_shipping_options[0]?.value
					: "",
			include_out_of_stock_products:
				amazonFilterProps?.includeOutOfStockProducts?.toString() ?? "",
			amazon_delivery_date:
				amazonFilterProps?.amazonProductDeliveryDay?.toString() ?? ""
		};

		setManufacturerFilters(selectedManufactures);
		setCategoryFilters(selectedCategories);
		setFeatureFilters(selectedFeatures);
		setFilterByConnectedDistributors(!!filterByConnectedDistributors);
		setExcludeDropShipWH(!!excludeDropShipWH);
		setExcludeOutOfStock(!!excludeOutOfStock);
		setSelectedProductType(selectedProductType);
		setSelfCatalogFilters(selfCatalogFilters);
		setCurrentCursor(null);
		const source =
			currentItemSource === ProductSource.Icecat
				? (isIcecatIntegrationEnabled &&
						(isIcecatLocaleFR ||
							(isIcecatLocaleUS && isFrenchLanguage))) ||
				  isIcecatEnabledForAll
					? ProductSource.Icecat
					: ProductSource.Etilize
				: currentItemSource;
		// Get current search input value to ensure we use the latest keywords
		const currentSearchValue =
			searchInputRef.current?.input?.value ||
			defaultTableFilterSortSearch.currentKeywords;

		const searchRequest = ProductSearchRequest.constructSearchRequest(
			currentSearchValue,
			source,
			null,
			shouldFetchRealtimePricing,
			shouldFilterByDistributors,
			(selectedDistributorsV2Data?.system as Distributor[]) || [],
			selectedCustomDistributors?.length
				? selectedCustomDistributors
				: selectedDistributorsV2Data?.custom,
			excludeOutOfStock,
			excludeDropShipWH,
			selectedManufactures,
			currentItemSource !== ProductSource.Amazon
				? selectedCategories
				: amazonFilterProps?.amazonProductCategory,
			selectedFeatures,
			itemTypeFilter,
			selfCatalogFilters,
			false,
			sortBy,
			amazonFilterProps?.amazonProductSubCategory,
			amazonSearchRequest
		);
		searchProductsHandle(searchRequest, false, true);
		setAmazonFilterState({
			includeOutOfStockProducts:
				amazonFilterProps?.includeOutOfStockProducts,
			eligibleForFreeShipping: amazonFilterProps?.eligibleForFreeShipping,
			amazonPrimeEligible: amazonFilterProps?.amazonPrimeEligible,
			amazonProductCategory: amazonFilterProps?.amazonProductCategory,
			amazonProductSubCategory:
				amazonFilterProps?.amazonProductSubCategory,
			amazonProductDeliveryDay:
				amazonFilterProps?.amazonProductDeliveryDay
		});
		setCustomDistributorsFilters(selectedCustomDistributors ?? []);
		setSelectedDistributorsV2(
			selectedDistributorsV2Data ?? { system: [], custom: [] }
		);
	};

	const handleProductRowEvent = (record: Product) => ({
		onClick: () => {
			if (record.product_source === ProductSource.ZomentumMarketplace) {
				const currentProductVendorStatus =
					vendorProgramDetails.find(v => {
						return v.id === record.vendor_company_id;
					})?.status ?? VendorSubscriptionStatus.NOTAPPLIED;
				if (
					currentProductVendorStatus !==
					VendorSubscriptionStatus.ACCEPTED
				)
					// should not select product since all unjoined vendor skus are disabled
					return;
			}
			props.itemType === ProductType.Bundle
				? setSelectedProductList([record])
				: setSelectedProductList(prevState =>
						prevState.some(product => product.id === record.id)
							? prevState.filter(
									product => product.id !== record.id
							  )
							: [...prevState, record]
				  );
		},
		onDoubleClick: () =>
			props.itemType === ProductType.Bundle ? submitProductData : null
	});

	const handleCreateProductAdded = (addedProduct: Product) => {
		const updatedDataSource =
			currentItemSource === ProductSource.Self
				? [addedProduct, ...dataSource]
				: dataSource;

		const updatedProductVsPricing = {
			...productVsPricing,
			[addedProduct.id]: getPreferredPricingForProduct(addedProduct)
		};

		setCreateProductButtonVisible(false);
		setDataSource(updatedDataSource);
		setProductVsPricing(updatedProductVsPricing);
	};

	const renderTableHeader = () => {
		const isSearchDisabled =
			currentItemSource === ProductSource.Amazon &&
			!isAmazonDistributorIntegrated;
		const amazonFilterValues = amazonFilterState;
		const amazonFilterProps = {
			includeOutOfStockProducts:
				amazonFilterValues?.includeOutOfStockProducts,
			eligibleForFreeShipping:
				amazonFilterValues?.eligibleForFreeShipping,
			amazonPrimeEligible: amazonFilterValues?.amazonPrimeEligible,
			amazonProductCategory: amazonFilterValues?.amazonProductCategory,
			amazonProductSubCategory:
				amazonFilterValues?.amazonProductSubCategory,
			amazonProductDeliveryDay:
				amazonFilterValues?.amazonProductDeliveryDay
		};

		return (
			<Row className="document-product-drawer-product-header w-full">
				<Col
					span={4}
					onClick={stopPropagation}
					className="document-product-drawer-product-source w-full"
				>
					<Select<ProductSource>
						getPopupContainer={trigger => trigger.parentNode}
						allowClear={false}
						loading={props.isLoading || loading}
						defaultValue={ProductSource.Self}
						value={currentItemSource}
						placeholder="Please choose the product source"
						onChange={onProductSourceChange}
						dropdownClassName="w-min"
						dropdownRender={menu => (
							<div>
								{menu}
								<ZFeatureAccess
									requiredRole={UserRole.SecondaryRole}
									requiredPlan={PricingPlanNames.IGNITE}
									permissions={createProductPermission}
								>
									<ZText
										className="create-product-dropdown-cta block"
										onClick={handleCreateProductClick}
									>
										Create Product
									</ZText>
								</ZFeatureAccess>
							</div>
						)}
						className="w-full"
					>
						{!props.isUnifiedSearchEnabled && (
							<Select.Option value={ProductSource.Self}>
								Self
							</Select.Option>
						)}
						{isIcecatIntegrationEnabled ? (
							<Select.Option value={ProductSource.Icecat}>
								{userCompany.settings
									.is_icecat_integration_enabled
									? "Global Catalogue"
									: "Global Catalogue (Formerly Etilize)"}
							</Select.Option>
						) : (
							<Select.Option value={ProductSource.Etilize}>
								Etilize
							</Select.Option>
						)}
						{marketplaceFlag && showPartnerAlignFeaturesFlag && (
							<Select.Option
								value={ProductSource.ZomentumMarketplace}
							>
								Align
							</Select.Option>
						)}
						{props.itemType !== ProductType.Bundle &&
							(hasPermissionForAmazonDistributor ||
								isInternalUser) && (
								<Select.Option value={ProductSource.Amazon}>
									Amazon
								</Select.Option>
							)}
						{enableCustomDistributorSeparateTabFlag &&
							!props.isUnifiedSearchEnabled && (
								<Select.Option
									value={ProductSource.CustomDistributor}
								>
									Custom Distributor
								</Select.Option>
							)}
					</Select>
				</Col>
				<Col
					span={20}
					className="document-product-drawer-product-button"
				>
					<Input.Search
						ref={searchInputRef}
						placeholder="Enter product name"
						enterButton={
							<Button
								type="primary"
								htmlType="button"
								icon={<SearchOutlined />}
								loading={props.isLoading || loading}
								disabled={isSearchDisabled}
							>
								Search
							</Button>
						}
						onSearch={currentKeywords =>
							onProductSearchKeywordChange(currentKeywords)
						}
						disabled={isSearchDisabled}
					/>
				</Col>
				{currentItemSource !== ProductSource.Amazon ||
				(isAmazonDistributorIntegrated &&
					currentItemSource === ProductSource.Amazon) ? (
					<Col span={24} className="mt-3">
						<ItemFiltersMenu
							isFromProductsPage={props.isFromProductsPage}
							currentItemSource={currentItemSource}
							keyWord={
								defaultTableFilterSortSearch.currentKeywords
							}
							metaInfo={metaInfo}
							manufacturerFilters={manufacturerFilters}
							categoryFilters={categoryFilters}
							featureFilters={featureFilters}
							filterByConnectedDistributors={
								filterByConnectedDistributors
							}
							excludeOutOfStock={excludeOutOfStock}
							excludeDropShipWH={excludeDropShipWH}
							onApply={onApplyFilter}
							selectedProductType={selectedProductType}
							selfCatalogFilters={selfCatalogFilters}
							disableLimitDistributorOption={
								!dataSource.length &&
								!filterByConnectedDistributors &&
								isDickerDataIntegrated
							}
							handleSortingOptionChange={
								handleSortingOptionChange
							}
							itemType={props.itemType}
							amazonFilterProps={amazonFilterProps}
							customDistributorsFilters={
								customDistributorsFilters
							}
							selectedDistributorsV2={selectedDistributorsV2}
							isCDSearchEnhancementEnabled={
								isCDSearchEnhancementEnabled
							}
						/>
					</Col>
				) : null}
			</Row>
		);
	};

	const etilizeProductsLoader = () => {
		return (
			<div className="flex justify-center items-center mb-7">
				<ZImage
					src={ZomentumLogo}
					alt="Fetching products..."
					className="h-7 w-7 mr-2"
					preview={false}
				/>
				Loading more products
				<span className="recursive-loader">
					<span className="dot dot-1">.</span>
					<span className="dot dot-2">.</span>
					<span className="dot dot-3">.</span>
				</span>
			</div>
		);
	};

	const handleSortingOptionChange = (value: SortOptions | undefined) => {
		if (currentItemSource === ProductSource.Icecat) {
			setSortBy(value);
			setCurrentCursor(null);
			setDataSource(prev => sortDataSource(prev, value));
			return;
		}
		let itemTypeFilter = selectedProductType;
		if (props.isFromProductsPage) {
			itemTypeFilter = findProductType(props.itemType);
		}
		setSortBy(value);
		setCurrentCursor(null);
		setDataSource([]);
		const shouldFetchRealtimePricing =
			currentItemSource !== ProductSource.Self;
		const shouldFilterByDistributors = false;

		const source =
			(currentItemSource as ProductSource) === ProductSource.Icecat
				? (isIcecatIntegrationEnabled &&
						(isIcecatLocaleFR ||
							(isIcecatLocaleUS && isFrenchLanguage))) ||
				  isIcecatEnabledForAll
					? ProductSource.Icecat
					: ProductSource.Etilize
				: currentItemSource;

		const searchRequest = ProductSearchRequest.constructSearchRequest(
			defaultTableFilterSortSearch.currentKeywords,
			source,
			null,
			shouldFetchRealtimePricing,
			shouldFilterByDistributors,
			(selectedDistributorsV2?.system as Distributor[]) || [],
			customDistributorsFilters?.length
				? customDistributorsFilters
				: selectedDistributorsV2?.custom,
			excludeOutOfStock,
			excludeDropShipWH,
			manufacturerFilters,
			categoryFilters,
			featureFilters,
			itemTypeFilter,
			selfCatalogFilters,
			false,
			value
		);
		searchProductsHandle(searchRequest, false, true);
	};

	const productRowSelection: TableRowSelection<Product> = {
		onChange: (
			selectedProductIdList: ReactText[],
			selectedProductsFromTable: Product[]
		) => {
			const updatedSelectedProducts = selectedProductIdList
				.map(productId => {
					const currentProductData = dataSource.find(
						product => product.id === productId
					);
					if (currentProductData) {
						return currentProductData;
					}
					const previousProductData = selectedProductList.find(
						product => product.id === productId
					);
					if (previousProductData) {
						return previousProductData;
					}
					return selectedProductsFromTable.find(
						product => product.id === productId
					);
				})
				.filter(product => product) as Product[];

			setSelectedProductList(updatedSelectedProducts);
		},
		type: props.itemType !== ProductType.Bundle ? "checkbox" : "radio",
		selectedRowKeys: selectedProductList.map(product => product?.id),
		columnWidth: 20,
		preserveSelectedRowKeys: true,
		getCheckboxProps: (record: Product) => {
			const isMarketplaceSku =
				record.product_source === ProductSource.ZomentumMarketplace;
			const currentProductVendorStatus =
				vendorProgramDetails.find(v => {
					return v.id === record.vendor_company_id;
				})?.status ?? VendorSubscriptionStatus.NOTAPPLIED;
			const isSkuDisabled =
				isMarketplaceSku &&
				currentProductVendorStatus !==
					VendorSubscriptionStatus.ACCEPTED;
			return {
				disabled: isSkuDisabled
			};
		}
	};

	const showRecursiveLoader =
		(props.isLoading ||
			loading ||
			!dataSource.length ||
			dataSource?.length < ETILIZE_PAGE_SIZE) &&
		!!currentCursor;

	return currentItemSource === ProductSource.Amazon &&
		!isAmazonDistributorIntegrated ? (
		<>
			<Row className="amazon-integrate-form-header">
				{renderTableHeader()}
			</Row>
			<IntegrateAmazonForm />
			<div className="ant-drawer-footer">
				<ZButton
					type="default"
					htmlType="button"
					onClick={onClose}
					disabled={props.isLoading || loading}
					className="mr-4"
				>
					Cancel
				</ZButton>
				<ZButton type="primary" htmlType="button" disabled={true}>
					Add Products
				</ZButton>
			</div>
		</>
	) : (
		<div ref={tableContainerRef}>
			<Row className="drawer-form-container no-padding">
				<Col
					span={24}
					className="table-page-container product-table-container"
				>
					<ConfigProvider
						renderEmpty={
							!loading ? customEmptyProductsTable : undefined
						}
					>
						<ZTable
							title={renderTableHeader}
							showHeader={false}
							loading={
								(props.isLoading || loading || !dataSource) &&
								!showRecursiveLoader
							}
							scroll={{ scrollToFirstRowOnChange: false }}
							size="small"
							dataSource={dataSource}
							columns={productColumns}
							rowKey="id"
							pagination={false}
							rowSelection={productRowSelection}
							onRow={handleProductRowEvent}
							className="product-table"
							footer={
								showRecursiveLoader
									? etilizeProductsLoader
									: undefined
							}
						/>
					</ConfigProvider>
				</Col>
			</Row>
			<div className="ant-drawer-footer">
				<ZButton
					type="default"
					htmlType="button"
					onClick={onClose}
					disabled={props.isLoading || loading}
					className="mr-4"
				>
					Cancel
				</ZButton>
				<ZButton
					type="primary"
					htmlType="button"
					loading={props.isLoading || loading || !dataSource}
					disabled={!selectedProductList.length}
					onClick={onSubmit}
				>
					{props.itemType === ProductType.Bundle
						? "Next"
						: "Add Products"}
				</ZButton>
			</div>
			<AddProductDrawerV2
				shouldRedirectToProduct={false}
				visible={createProductButtonVisible}
				onClose={handleOnClose}
				onAdd={handleCreateProductAdded}
				drawerState={EProductDrawerState.ADD}
			/>
			{props.itemType === ProductType.Bundle ? (
				props.configureDrawer
			) : (
				<ZUpdateMandatoryFieldsForQuoteBlockDrawer
					visible={
						(props.unfilledSelectedQuoteProductIds ?? []).length > 0
					}
					onClose={() => props.resetQuoteProducts?.()}
					renderProductColumn={renderProductColumn(true)}
					products={selectedProductList}
					unfilledProducts={selectedProductList.filter(product =>
						(props.unfilledSelectedQuoteProductIds ?? []).some(
							id => id === product.id
						)
					)}
					removeProducts={products =>
						removeItemsFromUpdateMandatoryFieldsDrawer(products)
					}
				>
					{props.configureDrawer}
				</ZUpdateMandatoryFieldsForQuoteBlockDrawer>
			)}
		</div>
	);
};

export default SearchDocumentProductCatalog;
